/**
 * The section chips — `docs/proposal.md` §6 item 4.
 *
 * Six at most — `Included 2 · Choose a plan 1 · Optional 1 · Total · Terms ·
 * Sign` — and **a chip whose section does not exist is not drawn**, so a
 * straightforward one-visit quote shows three rather than six greyed ones.
 *
 * The active chip follows the scroll with an `IntersectionObserver` rather
 * than a scroll handler: a handler has to decide what "the current section"
 * means on every frame, and the observer is told by the browser.
 *
 * `scrollIntoView` rather than arithmetic on `scrollTop`, because this page is
 * rendered inside three different scroll containers — the document on the
 * portal, the app frame's `<main>` in the builder, and a scaled preview frame
 * — and the one thing they agree on is that the browser knows where an
 * element is.
 */
import { useEffect, useState } from 'react'
import { cn } from '../../lib/cn.js'

export interface ProposalSection {
  id: string
  label: string
  /** Drawn as a small number after the label; omitted for Total, Terms, Sign. */
  count?: number
}

export function ProposalSectionNav({ sections, phone }: {
  sections: ProposalSection[]
  phone: boolean
}) {
  const [active, setActive] = useState<string | null>(sections[0]?.id ?? null)

  useEffect(() => {
    if (typeof IntersectionObserver !== 'function') return undefined
    const observer = new IntersectionObserver((entries) => {
      /*
       * The topmost section that is on screen wins. Taking the *most* visible
       * one instead makes the chip jump backwards when a long section scrolls
       * out under a short one, which reads as a bug rather than as a position.
       */
      const showing = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
      if (showing) setActive(showing.target.id)
    }, { rootMargin: '-96px 0px -60% 0px' })

    for (const section of sections) {
      const element = document.getElementById(section.id)
      if (element) observer.observe(element)
    }
    return () => observer.disconnect()
  }, [sections])

  if (sections.length < 2) return null

  return (
    <nav
      aria-label="Sections of this proposal"
      className={cn('sticky top-0 z-10 -mx-4 flex gap-2 overflow-x-auto bg-bg px-4 py-2',
        'no-scrollbar', phone ? '' : 'flex-wrap')}
    >
      {sections.map((section) => {
        const on = active === section.id
        return (
          <button
            key={section.id}
            type="button"
            aria-current={on ? 'true' : undefined}
            onClick={() => document.getElementById(section.id)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className={cn('inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5',
              'border text-[13.5px] font-semibold whitespace-nowrap',
              on ? 'border-brand bg-brand text-brand-on'
                : 'border-line bg-surface text-text-body hover:border-line-strong')}
          >
            {section.label}
            {section.count !== undefined && (
              <span className="text-[11.5px] opacity-80">{section.count}</span>
            )}
          </button>
        )
      })}
    </nav>
  )
}
